import { useEffect } from "react";
import { useMutation } from "@apollo/client";
import { HEARTBEAT } from "./queries";

const HEARTBEAT_INTERVAL_MS = 30_000;

/**
 * Pings the server every 30s while the tab is visible. The server
 * stamps `users.last_seen_at` on each ping; the UserType.online field
 * returns true when last_seen_at is within the last 75 seconds. That
 * window covers one missed ping.
 *
 * Pause-on-hidden so background tabs don't keep marking the user
 * online when they're not actually present.
 */
export function useHeartbeat(enabled: boolean) {
  const [heartbeat] = useMutation(HEARTBEAT);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    const ping = () => {
      if (cancelled) return;
      if (document.visibilityState === "hidden") return;
      heartbeat().catch(() => {
        // Silent — heartbeat failures don't need to disturb the user.
      });
    };

    ping();
    const id = setInterval(ping, HEARTBEAT_INTERVAL_MS);
    const onVisibility = () => ping();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, heartbeat]);
}
